import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fromLocalInput, toLocalInput } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Plane } from "lucide-react";

export default function NewWorkOrder() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    tail_number: "",
    aircraft_id: null,
    aircraft_model: "",
    anac_code: "",
    client_id: null,
    client_name: "",
    commander_id: null,
    commander_name: "",
    pilot_anac_code: "",
    passenger_count: 0,
    flight_origin: "",
    flight_destination: "",
    arrival_datetime: toLocalInput(new Date().toISOString()),
    estimated_departure: "",
    general_notes: "",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onTailChange = async (val) => {
    set("tail_number", val.toUpperCase());
    if (val.length >= 2) {
      try {
        const { data } = await api.get(`/aircraft`, { params: { q: val } });
        setSuggestions(data.slice(0, 6));
      } catch (err) {
        void err;
      }
    } else {
      setSuggestions([]);
    }
  };

  const applyAircraft = (a) => {
    setForm((prev) => ({
      ...prev,
      tail_number: a.tail_number || prev.tail_number,
      aircraft_id: a.id,
      aircraft_model: a.model || "",
      anac_code: a.anac_code || "",
      client_id: a.client_id || null,
      client_name: a.client_name || "",
      commander_id: a.default_commander_id || null,
      commander_name: a.default_commander_name || "",
    }));
    setSuggestions([]);
    // Best-effort lookup pilot anac code
    api.get(`/aircraft/lookup/${a.tail_number}`).then(({ data }) => {
      if (data?.pilot_anac_code) set("pilot_anac_code", data.pilot_anac_code);
    }).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        passenger_count: Number(form.passenger_count) || 0,
        arrival_datetime: fromLocalInput(form.arrival_datetime),
        estimated_departure: form.estimated_departure ? fromLocalInput(form.estimated_departure) : null,
      };
      const { data } = await api.post("/work-orders", payload);
      navigate(`/os/${data.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar OS");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <Button
        variant="ghost" size="sm" className="mb-4 -ml-2"
        onClick={() => navigate(-1)}
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
      </Button>
      <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Intake / Pouso</div>
      <h1 className="font-display font-black text-3xl tracking-tighter text-slate-950">Nova Ordem de Serviço</h1>
      <p className="text-sm text-slate-600 mt-1 mb-6">Digite o prefixo da aeronave para preenchimento automático.</p>

      <form onSubmit={submit} className="space-y-6" data-testid="new-wo-form">
        <Card className="p-5 rounded-sm border-slate-200">
          <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-3">Aeronave</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 relative">
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Prefixo</Label>
              <Input
                value={form.tail_number}
                onChange={(e) => onTailChange(e.target.value)}
                placeholder="PT-ABC"
                className="h-14 rounded-sm font-mono uppercase text-base"
                required
                data-testid="tail-number-input"
                autoComplete="off"
              />
              {suggestions.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-md max-h-64 overflow-y-auto">
                  {suggestions.map((a) => (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => applyAircraft(a)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      data-testid={`suggestion-${a.tail_number}`}
                    >
                      <div className="flex items-center gap-2">
                        <Plane className="w-3.5 h-3.5 text-slate-500" />
                        <span className="font-mono font-semibold text-sm">{a.tail_number}</span>
                        <span className="text-xs text-slate-500">{a.model}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{a.client_name || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Modelo</Label>
              <Input value={form.aircraft_model} onChange={(e) => set("aircraft_model", e.target.value)} className="h-14 rounded-sm" data-testid="model-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Código ANAC</Label>
              <Input value={form.anac_code} onChange={(e) => set("anac_code", e.target.value)} className="h-14 rounded-sm font-mono" data-testid="anac-code-input" />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-sm border-slate-200">
          <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-3">Cliente & Comandante</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Cliente / Proprietário</Label>
              <Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="h-14 rounded-sm" required data-testid="client-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Comandante</Label>
              <Input value={form.commander_name} onChange={(e) => set("commander_name", e.target.value)} className="h-14 rounded-sm" data-testid="commander-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Código ANAC do Piloto</Label>
              <Input value={form.pilot_anac_code} onChange={(e) => set("pilot_anac_code", e.target.value)} className="h-14 rounded-sm font-mono" data-testid="pilot-anac-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Passageiros (Pax)</Label>
              <Input type="number" min={0} value={form.passenger_count} onChange={(e) => set("passenger_count", e.target.value)} className="h-14 rounded-sm" data-testid="pax-input" />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-sm border-slate-200">
          <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-3">Voo & Horários</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Origem</Label>
              <Input value={form.flight_origin} onChange={(e) => set("flight_origin", e.target.value)} className="h-14 rounded-sm" placeholder="SBSP" data-testid="origin-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Destino Planejado</Label>
              <Input value={form.flight_destination} onChange={(e) => set("flight_destination", e.target.value)} className="h-14 rounded-sm" placeholder="SBRJ" data-testid="destination-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Chegada Real</Label>
              <Input type="datetime-local" value={form.arrival_datetime} onChange={(e) => set("arrival_datetime", e.target.value)} className="h-14 rounded-sm" required data-testid="arrival-input" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Partida Estimada</Label>
              <Input type="datetime-local" value={form.estimated_departure} onChange={(e) => set("estimated_departure", e.target.value)} className="h-14 rounded-sm" data-testid="departure-input" />
            </div>
          </div>
        </Card>

        <Card className="p-5 rounded-sm border-slate-200">
          <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Observações Gerais do Intake</Label>
          <Textarea
            value={form.general_notes}
            onChange={(e) => set("general_notes", e.target.value)}
            className="mt-2 rounded-sm min-h-24"
            placeholder="Discrepâncias visuais, atrasos, instruções do comandante…"
            data-testid="general-notes-input"
          />
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-sm text-sm">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="h-12 rounded-sm">Cancelar</Button>
          <Button
            type="submit"
            disabled={submitting}
            className="h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm px-6"
            data-testid="submit-wo-button"
          >
            {submitting ? "Criando…" : "Criar OS & Iniciar"}
          </Button>
        </div>
      </form>
    </div>
  );
}
